import Logo from "src/components/common/LogoSign";
import {
	Box,
	Container,
	Button,
	Menu,
	MenuItem,
	Typography,
	styled,
	useTheme,
	Input,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import UserBox from "src/components/common/UserBox";
import PersonIcon from "@mui/icons-material/Person";
import { isLoggedIn, isParentLoggedIn } from "src/services/auth.service";
import Link from "src/components/common/Link";
import { NavLink as RouterLink } from "react-router-dom";
import { useLocation, useNavigate } from "react-router";
import getParent from "src/models/Parent";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setSearch } from "src/features/home/program/programSlice";
import Cancel from "@mui/icons-material/Cancel";

const newsAndJobs = [
	{ to: "/blogs", label: "Article" },
	{ to: "/jobs", label: "Jobs" },
];

// Tes Love Language selalu paling bawah
const tools = [
	{ to: "/cv-builder", label: "CV Builder" },
	{ to: "/skd-readiness", label: "SKD Readiness" },
	{ to: "/interview-readiness", label: "Interview Readiness" },
	{ to: "/tes-kepribadian", label: "Tes Kepribadian" },
	{ to: "/tes-love-language", label: "Tes Love Language" },
];

const pathIsActive = (pathname, to) => {
	if (to === "/blogs") {
		return pathname === "/blogs" || pathname.startsWith("/blog/");
	}
	return pathname === to || pathname.startsWith(`${to}/`);
};

const NavbarWrapper = styled(Box)(
	({ theme }) => `
		top: 0;
		left: 0;
		right: 0;
		z-index: 99;
		background-color: ${theme.colors.alpha.trueWhite[100]};
`
);

const NavbarDesktop = ({ position }) => {
	const { search } = useSelector((state: any) => state.programSlice);
	const [searchValue, setSearchValue] = useState(search);
	const theme = useTheme();
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const navButtonSx = (isActive) => {
		const backgroundColor = isActive
			? theme.colors.primary.main
			: "transparent";
		const color = isActive
			? theme.colors.alpha.trueWhite[100]
			: theme.colors.alpha.black[100];

		return {
			minWidth: 0,
			margin: "0 2px",
			padding: "4px 8px",
			color,
			backgroundColor,
			fontWeight: isActive ? 700 : 500,
			whiteSpace: "nowrap",
			"&:hover": { backgroundColor },
			"& .MuiButton-endIcon": { ml: 0.25 },
		};
	};

	const NavButton = ({ to, label }) => {
		const isActive =
			(to.includes("/programs") && location.pathname.startsWith("/programs")) ||
			location.pathname === to;

		return (
			<Button
				disableRipple
				component={RouterLink}
				to={to}
				variant="text"
				color="primary"
				sx={navButtonSx(isActive)}>
				{label}
			</Button>
		);
	};

	const NavDropdown = ({ id, label, items }) => {
		const [anchorEl, setAnchorEl] = useState(null);
		const open = Boolean(anchorEl);
		const isActive = items.some((item) => pathIsActive(location.pathname, item.to));

		return (
			<>
				<Button
					id={`${id}-button`}
					disableRipple
					variant="text"
					color="primary"
					aria-controls={open ? `${id}-menu` : undefined}
					aria-haspopup="true"
					aria-expanded={open ? "true" : undefined}
					endIcon={<KeyboardArrowDownIcon fontSize="small" />}
					onClick={(event) => setAnchorEl(event.currentTarget)}
					sx={navButtonSx(isActive)}>
					{label}
				</Button>
				<Menu
					id={`${id}-menu`}
					anchorEl={anchorEl}
					open={open}
					onClose={() => setAnchorEl(null)}
					disableScrollLock
					MenuListProps={{ "aria-labelledby": `${id}-button` }}
					anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
					transformOrigin={{ vertical: "top", horizontal: "left" }}>
					{items.map((item) => (
						<MenuItem
							key={item.to}
							component={RouterLink}
							to={item.to}
							selected={pathIsActive(location.pathname, item.to)}
							onClick={() => setAnchorEl(null)}>
							{item.label}
						</MenuItem>
					))}
				</Menu>
			</>
		);
	};

	const Menus = () => (
		<>
			<NavButton to="/" label="Home" />
			<NavButton to="/programs" label="Programs" />
			<NavButton to="/testimony" label="Testimonies" />
			<NavDropdown id="news-jobs" label="Article & Jobs" items={newsAndJobs} />
			<NavDropdown id="tools" label="Tools" items={tools} />
			{isParentLoggedIn() && getParent() && (
				<NavButton
					to={`/report/member/${getParent().token}`}
					label="Report"
				/>
			)}
		</>
	);

	useEffect(() => {
		dispatch(setSearch({ search: "" }));
	}, [dispatch]);

	useEffect(() => {
		const isProgramsPage = location.pathname.startsWith("/programs");

		if (!isProgramsPage && search !== "") {
			dispatch(setSearch({ search: "" }));
			setSearchValue("");
		}
	}, [location.pathname, search, dispatch]);

	return (
		<NavbarWrapper
			sx={{
				background: "transparent",
				opacity: 0.85,
				mt: "16px",
				position,
			}}>
			<Container maxWidth="lg">
				<Box
					sx={{
						px: { lg: 2, xl: 3 },
						borderRadius: "40px",
						border: "1px solid #eee",
						boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.1)",
						backgroundColor: theme.colors.alpha.trueWhite[100],
						opacity: 0.9,
						height: 64,
						display: "flex",
						alignItems: "center",
						gap: 1,
						minWidth: 0,
					}}>
					<Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
						<Logo />
					</Box>

					<Box
						sx={{
							flex: 1,
							minWidth: 0,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							flexWrap: "nowrap",
						}}>
						<Menus />
					</Box>

					<Box
						sx={{
							flexShrink: 0,
							display: "flex",
							alignItems: "center",
							height: "100%",
							gap: 0.5,
						}}>
						<Input
							placeholder="Cari Program"
							sx={{ width: { lg: "15ch", xl: "16ch" }, fontSize: "0.9rem" }}
							startAdornment={
								<img
									src={`${process.env.REACT_APP_IMAGE_URL}static/images/placeholders/icons/search.svg`}
									alt="icon search"
									style={{ marginRight: "8px" }}
								/>
							}
							endAdornment={
								(searchValue ?? "").trim() !== "" ? (
									<Cancel
										sx={{ cursor: "pointer" }}
										onClick={() => {
											setSearchValue("");
											dispatch(setSearch({ search: "" }));
											navigate("/programs");
										}}
									/>
								) : null
							}
							value={searchValue}
							onChange={(e) => {
								setSearchValue(e.target.value);
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									navigate(`/programs`);
									dispatch(setSearch({ search: searchValue }));
								}
							}}
						/>

						{isLoggedIn() ? (
							<Box sx={{ display: "flex", alignItems: "center" }}>
								<UserBox nameUser={false} />
							</Box>
						) : (
							<Link to="/login">
								<Box sx={{ display: "flex", alignItems: "center", pl: 0.5 }}>
									<Typography variant="h6" sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>
										Sign In
									</Typography>
									<Box
										sx={{
											backgroundColor: theme.colors.primary.main,
											ml: 1,
											p: 0.25,
											borderRadius: "50%",
											height: 30,
											width: 30,
											display: "grid",
											placeItems: "center",
										}}>
										<PersonIcon sx={{ color: "#fff", fontSize: 20 }} />
									</Box>
								</Box>
							</Link>
						)}
					</Box>
				</Box>
			</Container>
		</NavbarWrapper>
	);
};

export default NavbarDesktop;
